import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AnnulerVenteInput,
  LigneRegistre,
  RegistreQuery,
  Vente,
  VenteInput,
} from '@sirajoptic/shared';
import { api } from '@/lib/api-client';
import { cleDashboard } from '@/features/dashboard/api/dashboard.api';
import { clesProduits } from '@/features/stock/api/stock.api';

export const clesVentes = {
  tous: ['ventes'] as const,
  registre: (q: Partial<RegistreQuery>) => ['ventes', 'registre', q] as const,
  une: (id: string) => ['ventes', id] as const,
};

/** Registre unifié : ventes au comptoir et commandes, réunies à la lecture. */
export function useRegistre(q: Partial<RegistreQuery>) {
  return useQuery({
    queryKey: clesVentes.registre(q),
    queryFn: () => api.liste<LigneRegistre>('/ventes/registre', q),
    placeholderData: (precedent) => precedent,
  });
}

export function useVente(id: string | null) {
  return useQuery({
    queryKey: clesVentes.une(id ?? ''),
    queryFn: () => api.get<Vente>(`/ventes/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Une vente touche au stock et au chiffre d'affaires : les deux se
 * rafraîchissent. Et quand elle est rattachée à une fiche client, elle entre
 * dans son historique et dans ses totaux — l'annuaire aussi doit se relire.
 */
function useInvalider() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: clesVentes.tous });
    void client.invalidateQueries({ queryKey: clesProduits.tous });
    void client.invalidateQueries({ queryKey: ['clients'] });
    void client.invalidateQueries({ queryKey: cleDashboard });
    void client.invalidateQueries({ queryKey: ['mouvements'] });
  };
}

export function useCreerVente() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (input: VenteInput) => api.post<Vente>('/ventes', input),
    onSuccess: invalider,
  });
}

export function useAnnulerVente() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AnnulerVenteInput }) =>
      api.post<Vente>(`/ventes/${id}/annuler`, input),
    onSuccess: invalider,
  });
}
